import { useEffect, useRef, useState } from "react";
import { ArrowRightIcon } from "../../Icons";
import MentionEditor, { MentionEditorHandle } from "../../Shared/MentionInput";
import {
  useGetPointsOfInterestQuery,
  useSendChatMessageMutation,
} from "../../../store/apiSlices/procurementAI";
import { useSearchParams } from "react-router-dom";

type SendChatMessageFn = ReturnType<typeof useSendChatMessageMutation>[0];

type ChatInputProps = {
  sendChatMessage: SendChatMessageFn;
  isLoading: boolean;
};

export default function ChatInput({
  sendChatMessage,
  isLoading,
}: ChatInputProps) {
  const [hasContent, setHasContent] = useState(false);

  const editorRef = useRef<MentionEditorHandle>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeThreadId = searchParams.get("threadId");

  const shouldSkip = !activeThreadId || activeThreadId?.startsWith("temp-");

  const { data } = useGetPointsOfInterestQuery(
    { thread_id: activeThreadId },
    {
      skip: shouldSkip,
    },
  );

  useEffect(() => {
    editorRef.current?.focus();
  }, [activeThreadId]);

  const handleSend = async () => {
    const value = editorRef.current?.getValue() || "";

    if (!value.trim()) return;

    const tempThreadId = "temp-" + crypto.randomUUID();

    const payload = {
      thread_id: activeThreadId,
      user_chat: value.trim(),
      temp_thread_id: !activeThreadId ? tempThreadId : null,
    };

    editorRef.current?.clear();
    if (!activeThreadId) {
      setSearchParams({ threadId: tempThreadId });
    }

    const { data } = await sendChatMessage(payload);

    if (data && data.status === "success") {
      setSearchParams({ threadId: data.thread_id });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // optional: allow Shift+Enter for newline
      if (e.shiftKey) {
        document.execCommand("insertLineBreak");
        return;
      }

      if (isLoading) return;

      handleSend();
    }
  };

  return (
    <div className="relative mx-1 mb-2">
      <MentionEditor
        ref={editorRef}
        triggers={[]}
        placeholder="Ask anything about procurement…"
        onInput={() => {
          const value = editorRef.current?.getValue() || "";

          setHasContent(!!value.trim());
        }}
        onKeyDown={handleKeyDown}
        className="pr-10"
        disabled={!!activeThreadId && data?.is_submitted !== 0}
      />

      <button
        onClick={handleSend}
        disabled={!hasContent || isLoading}
        className={`absolute right-1 bottom-1 rounded-full p-1 font-medium transition-all shrink-0 ${
          hasContent && !isLoading
            ? "bg-black hover:bg-black/70 text-white"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
        }`}
      >
        {isLoading ? (
          <span className="size-6 flex items-center justify-center">
            {/* simple spinner */}
            <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
          </span>
        ) : (
          <ArrowRightIcon className="size-6" />
        )}
      </button>
    </div>
  );
}
